import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, Row, Col } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getCssVariable, isLightColor } from '../../Utils/colorUtils';
import { updateSectionRoute } from '../../Utils/sectionUtils';
import { updateQuestionDetails } from '../../Utils/questionDetailsUpdater';
import { DEFAULT_RESPONSE_DETAILS } from '../../const';
import * as actions from '../../store/actions/AssessmentActions';
import styles from './McqSidePanel.scss';
import { setProgress, setQuestionDetails } from '../../store/actions';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const McqSidePanel = props => {
  const { t } = useTranslation('common');

  const {
    sectionHeading,
    questionDetails,
    isSelected,
    handleSidePanelClick,
    currentMcqId,
    currentMcqIds,
    isSaving,
    setSidePanelOpen,
    sectionId,
  } = props;

  const dispatch = useDispatch();
  const history = useHistory();
  const switchableSections = useSelector(state => state.assessment.switchableSections);
  const assessmentId = useSelector(state => state.invite.assessment_id);
  const { mcqsResponses, progress } = useSelector(state => state.assessment);

  const sections = useSelector(state => state.assessment.sections);
  const navigateToSection = () =>
    updateSectionRoute({
      sections,
      assessmentId,
      sectionId,
      history,
      handleSidePanelClick: null,
      handleMcqSidePanelClick,
    });

  const handleMcqSidePanelClick = () => {
    const newQuestionDetails = updateQuestionDetails(
      currentMcqIds,
      mcqsResponses,
      DEFAULT_RESPONSE_DETAILS,
    );

    dispatch(setQuestionDetails(newQuestionDetails));
    dispatch(setProgress({ ...progress, total: currentMcqIds.length }));
    dispatch(actions.getRecoredResponses(assessmentId));
  };

  const selectorMarker = getCssVariable('--selector-marker');
  const isLight = isLightColor(selectorMarker);

  const loading = !questionDetails || questionDetails.length === 0;

  const buttonBorderColor = (questionDetail, index) => {
    if (questionDetail.id === currentMcqId) {
      return '#1890FF';
    } else if (questionDetail.markedForReview) {
      return '#FAAD14';
    } else if (questionDetail.visited && questionDetail.answered) {
      return '#3EAEAD';
    } else if (questionDetail.visited && !questionDetail.answered) {
      return '#FF4D4F';
    } else {
      return '#1890FF';
    }
  };

  const colorAndBorder = (questionDetail, index) => {
    const color = buttonBorderColor(questionDetail, index);
    return {
      color,
      borderColor: color,
      borderWidth: questionDetail.id === currentMcqId ? '4px' : '2px',
    };
  };

  return (
    <div className={styles['c-side-panel']}>
      <div
        className={styles['c-side-panel-header']}
        style={{
          backgroundColor: isSelected ? 'var(--selector-marker)' : '#FFFFFF',
          color: isSelected ? (isLight ? 'black' : '#FFFFFF') : '#000000',
          cursor: isSelected ? 'pointer' : 'default',
        }}
        onClick={isSelected ? setSidePanelOpen : switchableSections ? navigateToSection : () => {}}
      >
        <h3>{sectionHeading}</h3>
        {isSelected ? <LeftOutlined /> : null}
      </div>

      {isSelected && (
        <div className={styles['c-side-panel-body']}>
          <Row gutter={[16, 16]}>
            {loading
              ? Array.from({ length: 6 }, (_, index) => (
                  <Col
                    span={8}
                    key={`skeleton-${index}`}
                  >
                    <Skeleton
                      circle
                      width={44}
                      height={44}
                    />
                  </Col>
                ))
              : questionDetails.map((questionDetail, index) => (
                  <Col
                    span={8}
                    key={`${sectionHeading}-${index}`}
                  >
                    <Button
                      shape="circle"
                      type="primary"
                      size="large"
                      onClick={() => {
                        handleSidePanelClick(questionDetail.id);
                      }}
                      style={colorAndBorder(questionDetail, index)}
                      ghost
                      disabled={isSaving}
                    >
                      {t('mcq.question_short_label')}
                      {index + 1}
                    </Button>
                  </Col>
                ))}
          </Row>
        </div>
      )}
    </div>
  );
};

export default McqSidePanel;
